import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreventiveMaintenanceAlerts = ({ equipment, workOrders, onRefresh }) => {
  const [alertFilter, setAlertFilter] = useState('all');

  const generateAlerts = () => {
    const alerts = [];
    const today = new Date();

    equipment?.forEach((eq) => {
      // Overdue maintenance
      if (eq?.next_maintenance_date) {
        const nextMaintenance = new Date(eq?.next_maintenance_date);
        const daysUntil = Math.ceil((nextMaintenance - today) / (1000 * 60 * 60 * 24));

        if (daysUntil < 0) {
          alerts?.push({
            id: `overdue-${eq?.id}`,
            type: 'overdue',
            severity: 'urgent',
            equipment: eq,
            title: 'Maintenance Overdue',
            message: `${eq?.name} maintenance is ${Math.abs(daysUntil)} days overdue`,
            daysUntil,
            actionRequired: true
          });
        } else if (daysUntil <= 7) {
          alerts?.push({
            id: `upcoming-${eq?.id}`,
            type: 'upcoming',
            severity: 'high',
            equipment: eq,
            title: 'Maintenance Due Soon',
            message: `${eq?.name} maintenance due in ${daysUntil} days`,
            daysUntil,
            actionRequired: true
          });
        } else if (daysUntil <= 14) {
          alerts?.push({
            id: `scheduled-${eq?.id}`,
            type: 'scheduled',
            severity: 'medium',
            equipment: eq,
            title: 'Scheduled Maintenance',
            message: `${eq?.name} maintenance scheduled in ${daysUntil} days`,
            daysUntil,
            actionRequired: false
          });
        }
      }

      // Equipment condition alerts
      if (eq?.condition === 'poor') {
        alerts?.push({
          id: `condition-${eq?.id}`,
          type: 'condition',
          severity: 'high',
          equipment: eq,
          title: 'Poor Equipment Condition',
          message: `${eq?.name} is in poor condition and requires attention`,
          actionRequired: true
        });
      }

      // Warranty expiration
      if (eq?.warranty_expiry) {
        const warrantyDate = new Date(eq?.warranty_expiry);
        const daysUntilExpiry = Math.ceil((warrantyDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry > 0 && daysUntilExpiry <= 30) {
          alerts?.push({
            id: `warranty-${eq?.id}`,
            type: 'warranty',
            severity: 'medium',
            equipment: eq,
            title: 'Warranty Expiring Soon',
            message: `${eq?.name} warranty expires in ${daysUntilExpiry} days`,
            daysUntil: daysUntilExpiry,
            actionRequired: false
          });
        }
      }
    });

    // Work order alerts
    workOrders?.forEach((wo) => {
      if (wo?.status === 'pending' && wo?.priority === 'urgent') {
        const equipment = equipment?.find(eq => eq?.id === wo?.equipment_id);
        alerts?.push({
          id: `workorder-${wo?.id}`,
          type: 'workorder',
          severity: 'urgent',
          equipment,
          workOrder: wo,
          title: 'Urgent Work Order',
          message: `${wo?.title} requires immediate attention`,
          actionRequired: true
        });
      }
    });

    return alerts?.sort((a, b) => {
      const severityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      return severityOrder?.[a?.severity] - severityOrder?.[b?.severity];
    });
  };

  const allAlerts = generateAlerts();

  const filteredAlerts = alertFilter === 'all'
    ? allAlerts
    : allAlerts?.filter(alert => alert?.severity === alertFilter);

  const getSeverityBadge = (severity) => {
    const config = {
      urgent: { label: 'Urgent', color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle' },
      high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertCircle' },
      medium: { label: 'Medium', color: 'text-primary', bg: 'bg-primary/10', icon: 'Info' },
      low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Bell' }
    };
    const cfg = config?.[severity] || config?.medium;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const alertStats = {
    urgent: allAlerts?.filter(a => a?.severity === 'urgent')?.length || 0,
    high: allAlerts?.filter(a => a?.severity === 'high')?.length || 0,
    medium: allAlerts?.filter(a => a?.severity === 'medium')?.length || 0,
    total: allAlerts?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Bell" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{alertStats?.total}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Total Alerts</p>
        </div>

        <div className="bg-card rounded-xl border border-error/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertTriangle" size={20} className="text-error" />
            <span className="text-2xl font-bold text-error">{alertStats?.urgent}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Urgent</p>
        </div>

        <div className="bg-card rounded-xl border border-warning/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertCircle" size={20} className="text-warning" />
            <span className="text-2xl font-bold text-warning">{alertStats?.high}</span>
          </div>
          <p className="text-sm font-medium text-foreground">High Priority</p>
        </div>

        <div className="bg-card rounded-xl border border-primary/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Info" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-primary">{alertStats?.medium}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Medium</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setAlertFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              alertFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            All Alerts ({alertStats?.total})
          </button>
          <button
            onClick={() => setAlertFilter('urgent')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              alertFilter === 'urgent' ? 'bg-error text-error-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Urgent ({alertStats?.urgent})
          </button>
          <button
            onClick={() => setAlertFilter('high')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              alertFilter === 'high' ? 'bg-warning text-warning-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            High ({alertStats?.high})
          </button>
          <button
            onClick={() => setAlertFilter('medium')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              alertFilter === 'medium' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Medium ({alertStats?.medium})
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts?.map((alert) => (
          <div
            key={alert?.id}
            className={`bg-card rounded-xl border p-4 md:p-6 hover:shadow-md transition-shadow ${
              alert?.severity === 'urgent' ? 'border-error/50' :
              alert?.severity === 'high' ? 'border-warning/50' : 'border-border'
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{alert?.title}</h3>
                  {getSeverityBadge(alert?.severity)}
                  {alert?.actionRequired && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      <Icon name="Zap" size={12} />
                      Action Required
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">{alert?.message}</p>

                {alert?.equipment && (
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                      <Icon name="Package" size={14} className="inline mr-1" />
                      {alert?.equipment?.name} - {alert?.equipment?.category}
                    </p>
                    <p>
                      <Icon name="MapPin" size={14} className="inline mr-1" />
                      {alert?.equipment?.location}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  variant={alert?.severity === 'urgent' ? 'danger' : 'primary'}
                  size="sm"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Schedule Now
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                >
                  View Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAlerts?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="CheckCircle2" size={48} className="mx-auto mb-4 text-success" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Alerts</h3>
          <p className="text-muted-foreground">
            {alertFilter === 'all' ?'All maintenance is up to date'
              : `No ${alertFilter} priority alerts at this time`}
          </p>
        </div>
      )}
    </div>
  );
};

export default PreventiveMaintenanceAlerts;